import React, { useCallback, useEffect, useState } from 'react';
import {
	EnvelopeIcon,
	PhoneIcon,
	TrashIcon,
	XMarkIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/20/solid';
import client from '../../api/client';
import Spinner from '../Global/Spinner';
import { useAlert } from '../../context/AlertContext';

function fmtDate(d) {
	if (!d) return '—';
	return new Date(d).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}

export default function Messages() {
	const { setAlert } = useAlert();
	const [messages, setMessages] = useState([]);
	const [loading, setLoading] = useState(false);
	const [q, setQ] = useState('');
	const [selected, setSelected] = useState(null); // the open message
	const [confirmId, setConfirmId] = useState(null); // pending delete
	const [deleting, setDeleting] = useState(false);

	const getMessages = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await client.get('/message');
			setMessages(data.messages || []);
		} catch (err) {
			setAlert('error', err?.response?.data?.message || 'Failed to load messages');
		} finally {
			setLoading(false);
		}
	}, [setAlert]);

	useEffect(() => {
		getMessages();
	}, [getMessages]);

	const handleDelete = async (id) => {
		setDeleting(true);
		try {
			await client.delete(`/message/${id}`);
			setMessages((prev) => prev.filter((m) => m._id !== id));
			if (selected?._id === id) setSelected(null);
			setAlert('success', 'Message deleted');
		} catch (err) {
			setAlert('error', err?.response?.data?.message || 'Could not delete message');
		} finally {
			setDeleting(false);
			setConfirmId(null);
		}
	};

	const filtered = messages.filter((m) => {
		if (!q.trim()) return true;
		const hay = `${m.name} ${m.email} ${m.phone} ${m.message}`.toLowerCase();
		return hay.includes(q.trim().toLowerCase());
	});

	return (
		<div className='px-4 pb-24 sm:px-6 lg:px-8'>
			<div>
				<h1 className='text-2xl font-semibold text-white'>Messages</h1>
				<p className='mt-1 text-sm text-gray-400'>
					Contact-form submissions. Click one to read it, or delete it.
				</p>
			</div>

			{/* Search */}
			<div className='mt-6 max-w-md'>
				<div className='relative'>
					<MagnifyingGlassIcon className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
					<input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder='Search name, email, message…'
						className='block w-full rounded-md border-0 bg-white/5 py-2 pl-9 pr-3 text-sm text-white ring-1 ring-inset ring-white/10 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-500'
					/>
				</div>
			</div>

			<div className='mt-6 overflow-hidden rounded-lg ring-1 ring-white/10'>
				{loading ? (
					<div className='py-16'>
						<Spinner color='white' />
					</div>
				) : filtered.length === 0 ? (
					<p className='py-16 text-center text-sm text-gray-400'>
						{messages.length === 0 ? 'No messages yet.' : 'No messages match.'}
					</p>
				) : (
					<table className='min-w-full divide-y divide-white/10'>
						<thead className='bg-white/5'>
							<tr>
								{['Name', 'Contact', 'Message', 'Received', ''].map((h) => (
									<th
										key={h}
										className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400'
									>
										{h}
									</th>
								))}
							</tr>
						</thead>
						<tbody className='divide-y divide-white/5'>
							{filtered.map((m) => (
								<tr
									key={m._id}
									onClick={() => setSelected(m)}
									className='cursor-pointer hover:bg-white/5'
								>
									<td className='px-4 py-3 text-sm font-medium text-white'>
										{m.name || '—'}
									</td>
									<td className='px-4 py-3 text-sm text-gray-300'>
										<div>{m.email}</div>
										<div className='text-gray-500'>{m.phone || ''}</div>
									</td>
									<td className='max-w-xs truncate px-4 py-3 text-sm text-gray-400'>
										{m.message}
									</td>
									<td className='whitespace-nowrap px-4 py-3 text-sm text-gray-400'>
										{fmtDate(m.createdAt)}
									</td>
									<td className='px-4 py-3 text-right'>
										<button
											onClick={(e) => {
												e.stopPropagation();
												setConfirmId(m._id);
											}}
											className='rounded p-1.5 text-gray-500 hover:bg-rose-500/10 hover:text-rose-400'
											title='Delete message'
										>
											<TrashIcon className='h-4 w-4' />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{selected && (
				<MessageDrawer
					message={selected}
					onClose={() => setSelected(null)}
					onDelete={() => setConfirmId(selected._id)}
				/>
			)}

			{confirmId && (
				<ConfirmDelete
					busy={deleting}
					onCancel={() => setConfirmId(null)}
					onConfirm={() => handleDelete(confirmId)}
				/>
			)}
		</div>
	);
}

function MessageDrawer({ message, onClose, onDelete }) {
	const m = message;
	return (
		<div className='fixed inset-0 z-50 flex justify-end'>
			<div
				className='absolute inset-0 bg-black/60'
				onClick={onClose}
				aria-hidden='true'
			/>
			<div className='relative flex h-full w-full max-w-lg flex-col overflow-y-auto bg-ink-900 shadow-xl ring-1 ring-white/10'>
				<div className='flex items-center justify-between border-b border-white/10 px-6 py-4'>
					<h2 className='text-lg font-semibold text-white'>Message</h2>
					<button
						onClick={onClose}
						className='rounded p-1 text-gray-400 hover:bg-white/10 hover:text-white'
					>
						<XMarkIcon className='h-5 w-5' />
					</button>
				</div>

				<div className='flex-1 space-y-6 px-6 py-6'>
					<div>
						<h3 className='text-xl font-semibold text-white'>
							{m.name || 'Unnamed'}
						</h3>
						<p className='mt-1 text-xs text-gray-500'>
							Received {fmtDate(m.createdAt)}
						</p>
					</div>

					<dl className='space-y-1.5 text-sm'>
						<Row label='Email'>
							{m.email ? (
								<a
									href={`mailto:${m.email}`}
									className='text-primary-400 hover:underline'
								>
									{m.email}
								</a>
							) : (
								'—'
							)}
						</Row>
						<Row label='Phone'>
							{m.phone ? (
								<a
									href={`tel:${m.phone}`}
									className='text-primary-400 hover:underline'
								>
									{m.phone}
								</a>
							) : (
								'—'
							)}
						</Row>
						<Row label='Address'>{m.address || '—'}</Row>
						<Row label='Heard via'>{m.heardFrom || '—'}</Row>
					</dl>

					<div>
						<h4 className='text-xs font-medium uppercase tracking-wide text-gray-400'>
							Message
						</h4>
						<div className='mt-2 whitespace-pre-wrap rounded-md bg-white/5 px-4 py-3 text-sm leading-6 text-gray-200 ring-1 ring-white/10'>
							{m.message || '—'}
						</div>
					</div>

					<div className='flex flex-wrap gap-3 pt-2'>
						{m.email && (
							<a
								href={`mailto:${m.email}`}
								className='inline-flex items-center gap-2 rounded-md bg-white/5 px-4 py-2 text-sm font-medium text-white ring-1 ring-white/10 hover:bg-white/10'
							>
								<EnvelopeIcon className='h-4 w-4' /> Email
							</a>
						)}
						{m.phone && (
							<a
								href={`tel:${m.phone}`}
								className='inline-flex items-center gap-2 rounded-md bg-white/5 px-4 py-2 text-sm font-medium text-white ring-1 ring-white/10 hover:bg-white/10'
							>
								<PhoneIcon className='h-4 w-4' /> Call
							</a>
						)}
						<button
							onClick={onDelete}
							className='inline-flex items-center gap-2 rounded-md bg-rose-500/10 px-4 py-2 text-sm font-medium text-rose-300 ring-1 ring-rose-500/30 hover:bg-rose-500/20'
						>
							<TrashIcon className='h-4 w-4' /> Delete
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function ConfirmDelete({ onCancel, onConfirm, busy }) {
	return (
		<div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4'>
			<div className='w-full max-w-sm rounded-lg bg-ink-900 p-6 ring-1 ring-white/10'>
				<h3 className='text-base font-semibold text-white'>Delete this message?</h3>
				<p className='mt-2 text-sm text-gray-400'>
					This permanently removes the submission. It can&apos;t be undone.
				</p>
				<div className='mt-6 flex justify-end gap-3'>
					<button
						onClick={onCancel}
						disabled={busy}
						className='rounded-md px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/10'
					>
						Cancel
					</button>
					<button
						onClick={onConfirm}
						disabled={busy}
						className='rounded-md bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50'
					>
						{busy ? 'Deleting…' : 'Delete'}
					</button>
				</div>
			</div>
		</div>
	);
}

function Row({ label, children }) {
	return (
		<div className='flex gap-2'>
			<dt className='w-20 shrink-0 text-gray-500'>{label}</dt>
			<dd className='text-gray-200'>{children}</dd>
		</div>
	);
}
